import { SHA256, enc } from "crypto-js";
import { createClient } from "graphql-ws";
import { LoaderFunction } from "react-router-dom";
import {
  ApolloLink,
  ServerError,
  createHttpLink,
  from,
  fromPromise,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { endpoints, routes } from "@songtradr-one/shared-common";
import { AppStorage } from "../helpers/app-storage";
import { getApollo } from "../helpers/get-apollo";
import { getSsoClient } from "../helpers/get-sso-client";
import { AppLayoutConfig } from "../layouts/App.Parts";
import { telemetryBus } from "./telemetry-bus";

const shouldFlagSlow = (serverTiming: string | null) =>
  !!serverTiming && /slow/i.test(serverTiming);

/**
 * A closure that returns a React Router LoaderFunction responsible for initializing
 * an Apollo Client singleton and preloading its query function using the provided environment variables
 *
 * @param config an object containing the app configuration
 *
 * @returns a React Router LoaderFunction that prepares an Apollo client and preloads its query function
 */
export const createApolloLoader =
  ({ appId, production, coreBackendUrl }: AppLayoutConfig): LoaderFunction =>
  () => {
    const ssoClient = getSsoClient();
    const graphqlUrl = `${coreBackendUrl}${endpoints.graphql}`;

    /** This link is responsible for setting up the HTTP transport for GraphQL */
    const httpLink = createHttpLink({
      uri: (operation) =>
        production ? graphqlUrl : `${graphqlUrl}?${operation.operationName}`,
      credentials: "include",
      fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
        const res = await fetch(input as RequestInfo, init);
        const st = res.headers.get("server-timing");
        if (shouldFlagSlow(st)) {
          const clone = res.clone();
          const jsonResult = await clone.json();
          telemetryBus.publish({
            kind: "server-timing",
            level: "warn",
            text: "Server reported slow processing (Server‑Timing).",
            raw: st,
            jsonResult,
          });
        }
        return res;
      },
    });

    const persistedQueryLink = createPersistedQueryLink({
      sha256: (query) => SHA256(query).toString(enc.Hex),
    });

    /** This link is responsible for setting up the WebSocket transport for GraphQL subscriptions */
    const wsLink = new GraphQLWsLink(
      createClient({
        url: graphqlUrl.replace("http", "ws"),
        /** Our fargate servers occasional recycle which would cause the
         * connection to be closed. This will retry the connection
         * indefinitely until a new connection is established.
         */
        shouldRetry: () => true,
        retryAttempts: Infinity,
        retryWait: async (_retries) => {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        },
        connectionParams: () => {
          return {
            headers: {
              "x-app-id": appId,
            },
          };
        },
      }),
    );

    /** This link is used to start the auth exchange when a token is present in the params */
    const authLink = setContext((_, { headers }) => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      const _headers: Record<string, string> = {
        ...(headers ?? {}),
      };
      delete _headers.authorization; // Always remove existing auth header in case of retry which would have been already processed

      if (token) {
        _headers.authorization = token;
        urlParams.delete("token");
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${
            urlParams.size ? "?" + urlParams.toString() : ""
          }`,
        );
      }

      // The share token is the path segment after the resource type under the
      // public shared route (e.g. /music/shared/track/{shareToken}); read it
      // fresh per request.
      const sharedBase = `${routes.music.shared.base}/`;
      const { pathname } = window.location;
      const shareToken = pathname.startsWith(sharedBase)
        ? pathname.slice(sharedBase.length).split("/")[1]
        : undefined;

      const currentOrgId = AppStorage.currentOrgId;
      const currentPath = AppStorage.getCurrentPath();
      const previousPath = AppStorage.previousPath;

      const newHeaders = {
        headers: {
          ..._headers,
          "x-app-id": appId,
          "x-request-utc": new Date().toISOString(),
          "x-device-id": AppStorage.deviceId,
          "x-session-id": AppStorage.sessionId,
          ...(currentOrgId ? { "x-current-org-id": currentOrgId } : {}),
          ...(shareToken ? { "x-share-token": shareToken } : {}),
          ...(currentPath !== previousPath
            ? { "x-client-path": currentPath }
            : {}),
        },
      };

      AppStorage.previousPath = currentPath;

      return newHeaders;
    });

    const removeTypenameLink = new ApolloLink((operation, forward) => {
      if (operation.variables) {
        const strip = <T>(input: T): T => {
          if (Array.isArray(input)) {
            return input.map(strip) as unknown as T;
          }
          if (input !== null && typeof input === "object") {
            const result = {} as Record<string, unknown>;
            for (const key in input) {
              if (key !== "__typename") {
                const value = input[key as keyof T];
                result[key] = strip(value);
              }
            }
            return result as T;
          }
          return input;
        };

        operation.variables = strip(operation.variables);
      }
      return forward(operation);
    });

    /**
     * This link is used to attempt to refresh the user's authentication token
     * when a 401 Unauthorized error is encountered.
     *
     * It uses a singleton promise to ensure that only one refresh attempt is made at a time.
     * If the refresh is successful, it retries the original request as well as any that came
     * through while the singleton promise was pending.
     * If the refresh fails, it redirects the user to the sign-in page.
     */
    let refresher: Promise<void> | null = null;
    const authErrorLink = onError(({ networkError, operation, forward }) => {
      const errRes = (networkError as ServerError)?.response as
        | Response
        | undefined;
      const st = errRes?.headers?.get?.("server-timing") ?? null;
      if (shouldFlagSlow(st)) {
        telemetryBus.publish({
          kind: "server-timing",
          level: "warn",
          text: "Server reported slow processing (error response).",
          raw: st,
        });
      }
      const statusCode = (networkError as ServerError)?.statusCode;
      if (statusCode === 401 || statusCode === 498) {
        if (!refresher) {
          refresher = (async () => {
            try {
              if (statusCode === 498) {
                //498 > Invalid JWT thrown by jwt.middleware.ts
                await ssoClient.actions.signOut(window.location.origin);
              } else {
                //401 > Unauthorized or expired token
                await ssoClient.requests.refreshToken();
              }
            } catch (err) {
              //AppStorage.clear(); //CLEAR APP STORAGE
              await ssoClient.actions.signInWithWorkspace(window.location.href);
            } finally {
              refresher = null;
            }
          })();
        }
        return fromPromise(refresher).flatMap(() => forward(operation));
      }
    });

    /** This is just error handling boiler plate from the apollo docs
     *  @todo Relevant ticket: https://songtradr.atlassian.net/jira/software/projects/SO/boards/98?selectedIssue=SO-198&text=error
     */
    const errorLink = onError(({ networkError }) => {
      if (networkError && !production) {
        if (!networkError.message.includes("PersistedQueryNotFound")) {
          console.log(`[Network error]: ${networkError}`);
        }
      }
    });

    /**
     * This link uses the Apollo split function to route
     * GraphQL operations to the appropriate transport
     * mechanism based on their type. If the operation is
     * a subscription, it is routed to the wsLink (WebSocket link)
     * to enable real-time updates. If the operation is a
     * query or mutation, it is routed to the HTTP link
     * chain (from([...])), which includes authentication,
     * error handling, and other middleware.
     */
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      from([
        removeTypenameLink,
        persistedQueryLink,
        authLink,
        authErrorLink,
        errorLink,
        httpLink,
      ]),
    );

    /** Calling this function initializes the Apollo Client singleton */
    getApollo(splitLink);
  };
